// Cross-field and sport-aware validation of LeagueSettings.
// Returns every problem at once so the rule editor can show them all.

import { z } from 'zod';
import { CATEGORY_FORMATS, type SportDefinition } from '@fsp/sports-core';
import type { LeagueSettings } from './schemas';
import { bracketShape } from '../schedule/bracket';
import { MAX_DIVISIONS } from './divisions';
import { scoringLimitIssues, startingPositions } from './scoring-table';
import { isTimeZone } from '../time/zone';
import { playsTwoOpponents } from '../matchup/format';

/**
 * One thing wrong, or worth a second look, in a settings object.
 *
 * Zod rather than an interface since 2026-09-17, for the same reason as
 * `RulesDocument`: `POST /api/leagues/preview` serves these, so they are part
 * of the generated contract.
 */
export const Issue = z.object({
  /** Dot path into LeagueSettings: 'scoring.units[3].stat' */
  path: z.string(),
  severity: z.enum(['error', 'warning']),
  message: z.string(),
});
export type Issue = z.infer<typeof Issue>;

/** Whether two lists of plain values are the same, in the same order. */
const sameList = (a: readonly unknown[], b: readonly unknown[]): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/**
 * Everything wrong with a league's settings. `before` is the version it
 * would replace, when there is one: a scoring rule exactly as it was there is
 * not held to the limits again (`scoringLimitIssues`), so a league whose
 * scoring predates them can still save anything else.
 */
export function validateLeagueSettings(
  s: LeagueSettings,
  sport: SportDefinition,
  before?: LeagueSettings,
  /**
   * What the rules alone cannot say. `carried`: a season started from the
   * last (`LeagueRef.lastSeasonId`), whose dynasty carried every roster, so
   * its draft fills what each team adds rather than a roster
   * (PLAN-2026-10-27-the-next-season §2.4 rule 2).
   */
  context: { carried?: boolean } = {},
): Issue[] {
  const issues: Issue[] = [...scoringLimitIssues(s.scoring, sport, before?.scoring)];
  const err = (path: string, message: string) => issues.push({ path, severity: 'error', message });
  const warn = (path: string, message: string) =>
    issues.push({ path, severity: 'warning', message });

  const stats = new Map(sport.stats.map((st) => [st.key, st]));
  const positions = new Set(sport.positions.map((p) => p.id));
  const slots = new Map(sport.slots.map((sl) => [sl.id, sl]));
  const statuses = new Set(sport.injuryStatuses.map((st) => st.id));

  if (s.sport !== sport.id) err('sport', `Settings are for ${s.sport}, sport is ${sport.id}`);
  // A ZONE THIS RUNTIME CAN NAME (the audit of 2026-10-20): every lock, waiver
  // run and week boundary is worked out in it, and one it cannot format
  // failed each of them. Checked here, at a save, a league made and a league
  // brought in from a file, rather than in the stored shape, so a version
  // already stored still reads.
  if (!isTimeZone(s.identity.timeZone)) err('identity.timeZone', 'Pick a time zone from the list.');

  // ── Format ──
  if (!sport.supportedFormats.includes(s.format.id)) {
    err('format.id', `${sport.name} does not support the ${s.format.id} format`);
  }
  const isCategoryFormat = CATEGORY_FORMATS.has(s.format.id);
  if (isCategoryFormat && !s.categories) err('categories', 'This format needs a categories setup');
  if (s.format.id === 'elimination') {
    if (s.identity.teamCount < 3) err('identity.teamCount', 'Elimination needs at least 3 teams');
    if (!s.format.elimination) err('format.elimination', 'Elimination settings are missing');
  }
  // TWO OPPONENTS A WEEK (Q6) needs somebody to be the second.
  if (playsTwoOpponents(s) && s.identity.teamCount < 3)
    err('format.h2h.twoOpponents', 'Two opponents a week needs at least 3 teams');
  if (s.format.id === 'best-ball' && !s.lineup.bestBall) {
    warn('lineup.bestBall', 'Best-ball format usually turns on best-ball lineups');
  }

  // ── Scoring ──
  const positionsOk = (path: string, list: readonly string[] | null) => {
    for (const p of list ?? []) if (!positions.has(p)) err(path, `Unknown position ${p}`);
  };
  s.scoring.units.forEach((u, i) => {
    const st = stats.get(u.stat);
    const path = `scoring.units[${i}]`;
    if (!st) return err(`${path}.stat`, `Unknown stat ${u.stat}`);
    if (!st.scorable) err(`${path}.stat`, `${st.name} cannot be scored`);
    if (st.kind === 'ratio') err(`${path}.stat`, `${st.name} is a ratio; score its parts instead`);
    if (st.kind === 'tiered') err(`${path}.stat`, `${st.name} is scored by bands; use a tier rule`);
    // No warning for a count of N with no rounding: "1 point every 20 yards"
    // is how the scoring editor keeps the unit a commissioner chose
    // (PLAN-2026-10-18 §3), and it scores exactly as a point a yard would.
    positionsOk(`${path}.positions`, u.positions);
  });
  s.scoring.bonuses.forEach((b, i) => {
    const st = stats.get(b.stat);
    const path = `scoring.bonuses[${i}]`;
    if (!st) return err(`${path}.stat`, `Unknown stat ${b.stat}`);
    if (!st.scorable || st.kind === 'ratio' || st.kind === 'tiered')
      err(`${path}.stat`, `${st.name} cannot carry a bonus`);
    positionsOk(`${path}.positions`, b.positions);
  });
  s.scoring.tiers.forEach((t, i) => {
    const st = stats.get(t.stat);
    const path = `scoring.tiers[${i}]`;
    if (!st) return err(`${path}.stat`, `Unknown stat ${t.stat}`);
    if (st.kind !== 'tiered') err(`${path}.stat`, `${st.name} is not a banded stat`);
    positionsOk(`${path}.positions`, t.positions);
    const bands = [...t.bands].sort((a, b) => a.min - b.min);
    // AND THEY MUST COVER THE BOTTOM. RULES_SCHEMA §16 asks for bands that
    // "don't start at the stat's minimum" to be an error, and `StatDef` has no
    // minimum field — so the rule went unwritten and a band set starting at 1
    // scored a shutout as nothing at all, which is the one result a
    // points-allowed rule most obviously has an opinion about.
    //
    // ZERO IS THE FLOOR OF A COUNTING STAT, and every `tiered` stat is one:
    // points allowed, yards allowed. Reading the floor from the kind rather
    // than from a declared minimum is what makes this implementable today. A
    // sport that one day bands a stat which can go negative will need that
    // field on `StatDef`, and this is the line that will say so.
    const first = bands[0];
    if (first !== undefined && first.min !== 0) {
      err(
        `${path}.bands`,
        `Bands start at ${first.min}, so nothing scores below it. The lowest band must start at 0`,
      );
    }
    for (let k = 1; k < bands.length; k++) {
      const prev = bands[k - 1]!;
      const cur = bands[k]!;
      // A BAND HOLDS ITS MIN AND ITS MAX (`points.ts`), so the next starts one
      // past the last's max. Two that touch — 0 to 10, then 10 to 20 — both
      // held 10, and the engine took the first; RULES_SCHEMA §16 calls it an
      // overlap (the audit of 2026-10-20, C-4).
      if (prev.max === null) err(`${path}.bands`, 'Only the last band may be open-ended');
      else if (prev.max + 1 !== cur.min)
        err(`${path}.bands`, `Gap or overlap between ${prev.max} and ${cur.min}`);
    }
  });
  // Ambiguity: two position-specific unit rules for the same stat that both
  // name a position, so a player there matches both and the first wins. Rules
  // for different positions are not ambiguous to a one-position player, and
  // the scoring editor writes exactly those (a tight end's catch and a
  // running back's); a sport whose players hold several positions at once
  // would want this widened to any two lists such a player could span.
  const byStat = new Map<string, string[][]>();
  for (const u of s.scoring.units) {
    if (u.positions) byStat.set(u.stat, [...(byStat.get(u.stat) ?? []), [...u.positions]]);
  }
  for (const [stat, lists] of byStat) {
    const overlap = lists.some((a, i) =>
      lists.slice(i + 1).some((b) => a.some((p) => b.includes(p))),
    );
    if (overlap) warn('scoring.units', `Several position rules for ${stat}; the first match wins`);
  }
  if (!isCategoryFormat) {
    const ruled = new Set(
      [...s.scoring.units, ...s.scoring.bonuses, ...s.scoring.tiers].map((r) => r.stat),
    );
    // A SCORABLE STAT WITH NO RULE AT ALL, which RULES_SCHEMA §16 has always
    // asked for and which this loop did not do: the body was a comment saying
    // "presets decide what those are" and nothing else, so a commissioner who
    // deleted the `rec` rule got a silent zero per reception (AUDIT-2026-09-19
    // F7). The presets DO decide, and this is what reading them looks like.
    //
    // EVERY PRESET, NOT ANY. A stat some presets price and others deliberately
    // do not is the case this must stay quiet about: `rec` is priced by four
    // of football's five presets and pointedly not by `standard`, which is
    // non-PPR and is one of the most ordinary leagues there is. The first
    // draft of this used "any preset" and warned every standard league about
    // receptions — a warning nobody can act on, shown to somebody who chose
    // it on purpose. What the intersection means is narrower and true: the
    // sport has no preset that leaves this unpriced, so a league that leaves
    // it unpriced has probably lost a rule rather than made a decision.
    const pricedBy = (preset: (typeof sport.scoringPresets)[number]): Set<string> =>
      new Set([
        ...preset.config.units.map((u) => u.stat),
        ...preset.config.bonuses.map((b) => b.stat),
        ...preset.config.tiers.map((t) => t.stat),
      ]);
    //
    // AND ONLY OVER THE PRESETS FOR ITS POSITIONS (the review of Q11's
    // backend). With presets for a league of defensive players, which price
    // no team defense, "every preset" stopped pricing the team defense's
    // stats and a standard league that lost its interception rule was no
    // longer told. So a stat is judged against the presets that price
    // anything its positions make, and only in a league that starts one of
    // them: a standard league is not told it prices no tackles.
    const priced = sport.scoringPresets.map(pricedBy);
    const everyPosition = sport.positions.map((p) => p.id);
    const positionsOf = (key: string) => stats.get(key)?.positions ?? everyPosition;
    const starts = new Set(startingPositions(s.roster, sport));
    for (const st of sport.stats) {
      if (!st.scorable || st.kind === 'ratio' || st.category === 'research' || ruled.has(st.key))
        continue;
      const its = positionsOf(st.key);
      if (!its.some((p) => starts.has(p))) continue;
      // The presets for its positions: those pricing a stat made by its
      // positions alone (a sack is the defenders'; a return is anybody's).
      const relevant = priced.filter((set) =>
        [...set].some((key) => positionsOf(key).every((p) => its.includes(p))),
      );
      if (relevant.length > 0 && relevant.every((set) => set.has(st.key)))
        warn('scoring', `Nothing scores ${st.name}. Every ${st.shortName} is worth zero`);
    }
  }

  // ── Categories ──
  if (s.categories) {
    s.categories.categories.forEach((c, i) => {
      const st = stats.get(c.stat);
      if (!st) return err(`categories.categories[${i}].stat`, `Unknown stat ${c.stat}`);
      if (!st.categorizable)
        err(`categories.categories[${i}].stat`, `${st.name} cannot be a category`);
    });
  }

  // ── Roster ──
  let starterSlots = 0;
  s.roster.slots.forEach((rs, i) => {
    const sl = slots.get(rs.slot);
    if (!sl) return err(`roster.slots[${i}].slot`, `Unknown slot ${rs.slot}`);
    if (sl.kind === 'starter') starterSlots += rs.count;
  });
  if (starterSlots === 0) err('roster.slots', 'A roster needs at least one starting slot');
  for (const p of Object.keys(s.roster.maxPerPosition)) {
    if (!positions.has(p)) err('roster.maxPerPosition', `Unknown position ${p}`);
  }
  if (s.roster.reserve) {
    const sl = slots.get(s.roster.reserve.slot);
    if (!sl || sl.kind !== 'reserve')
      err('roster.reserve.slot', 'Reserve slot must be a reserve-kind slot');
    for (const st of s.roster.reserve.eligibleStatuses) {
      if (!statuses.has(st)) err('roster.reserve.eligibleStatuses', `Unknown status ${st}`);
    }
  }
  if (s.roster.taxi) {
    const sl = slots.get(s.roster.taxi.slot);
    if (!sl || sl.kind !== 'taxi') err('roster.taxi.slot', 'Taxi slot must be a taxi-kind slot');
  }

  // ── Lineup ──
  if (!sport.lineupPeriods.includes(s.lineup.period)) {
    err('lineup.period', `${sport.name} lineups cannot be ${s.lineup.period}`);
  }
  if (s.lineup.lockRule === 'fixed-time' && !s.lineup.fixedLockTime) {
    err('lineup.fixedLockTime', 'Fixed-time lock needs a time');
  }
  s.lineup.gamesPlayedCaps.forEach((cap, i) =>
    positionsOk(`lineup.gamesPlayedCaps[${i}].positions`, cap.positions),
  );

  // ── Schedule and playoffs ──
  const rs = s.schedule.regularSeason;
  if (rs.firstPeriod > rs.lastPeriod)
    err('schedule.regularSeason', 'The first week is after the last week');
  const teams = s.identity.teamCount;
  const po = s.playoffs;
  if (po.teamCount > teams) err('playoffs.teamCount', 'More playoff teams than teams');
  if (po.teamCount > 0) {
    if (po.byes > po.teamCount / 2)
      err('playoffs.byes', 'Too many byes for that many playoff teams');
    // THE BYE COUNT IS NOT A CHOICE, it is what is left over when the field is
    // laid into the smallest bracket that fits it: six teams in an eight-team
    // bracket rests two, and no other number is possible. SCHEDULE.md said
    // this validator enforced that; until 2026-09-19 it did not, and a league
    // could store a bye count the bracket would quietly ignore.
    const shape = bracketShape(po.teamCount);
    if (po.byes !== shape.byes)
      err(
        'playoffs.byes',
        `${po.teamCount} playoff teams fill a ${shape.size}-team bracket, which rests ${shape.byes}`,
      );
    const roundsNeeded =
      Math.ceil(Math.log2(Math.max(po.teamCount, 2))) + (po.championshipPeriods - 1);
    if (po.periods.length < roundsNeeded)
      err('playoffs.periods', `Need ${roundsNeeded} playoff weeks for ${po.teamCount} teams`);
    for (const p of po.periods) {
      if (p <= rs.lastPeriod)
        err('playoffs.periods', `Playoff week ${p} overlaps the regular season`);
    }
    // EVERY WINNER IN (B-563): division winners first needs divisions, and a
    // field no smaller than them, or it seeds a winner out of the place it
    // promised. Refused when the seeding, the field or the divisions change;
    // a version stored before this rule says it as a warning, so a save of
    // anything else still goes through.
    const seedingKept =
      before !== undefined &&
      before.playoffs.seeding === po.seeding &&
      before.playoffs.teamCount === po.teamCount &&
      sameList(before.schedule.divisions, s.schedule.divisions);
    const seedingIssue = seedingKept ? warn : err;
    if (po.seeding === 'division-winners-first' && s.schedule.divisions.length === 0)
      seedingIssue('playoffs.seeding', 'Division winners first needs divisions');
    else if (po.seeding === 'division-winners-first' && s.schedule.divisions.length > po.teamCount)
      seedingIssue(
        'playoffs.seeding',
        `Division winners first needs a playoff place for each of the ${s.schedule.divisions.length} division winners; ${po.teamCount} teams make the playoffs`,
      );
  }
  // ── Divisions (PLAN-2026-10-29-divisions §2) ──
  const divisions = s.schedule.divisions;
  if (divisions.length === 1)
    err('schedule.divisions', 'One division is the whole league. Add a second, or take it away');
  if (divisions.length > MAX_DIVISIONS)
    err('schedule.divisions', `At most ${MAX_DIVISIONS} divisions`);
  else if (divisions.length > 1 && divisions.length > Math.floor(teams / 2))
    // A SEAT CLOSED before the draft can leave divisions that fitted the
    // league as it was made: said as a warning until they change, so the
    // league is told and a save of anything else still goes through (the
    // review of B-561).
    (before !== undefined && sameList(before.schedule.divisions, divisions) ? warn : err)(
      'schedule.divisions',
      `${teams} teams make at most ${Math.floor(teams / 2)} divisions; each needs two teams`,
    );
  if (divisions.some((d) => d.name.trim() === ''))
    err('schedule.divisions', 'Every division needs a name');
  const named = new Map<string, number>();
  for (const d of divisions) {
    const key = d.name.trim().toLowerCase();
    if (key !== '') named.set(key, (named.get(key) ?? 0) + 1);
  }
  for (const d of divisions) {
    if ((named.get(d.name.trim().toLowerCase()) ?? 0) > 1) {
      err('schedule.divisions', `Two divisions are called ${d.name.trim()}`);
      break;
    }
  }
  if (new Set(divisions.map((d) => d.id)).size !== divisions.length)
    err('schedule.divisions', 'Two divisions share an id');
  for (const p of s.schedule.rivalryPeriods) {
    if (p < rs.firstPeriod || p > rs.lastPeriod)
      warn('schedule.rivalryPeriods', `Rivalry week ${p} is outside the regular season`);
  }

  // ── Tiebreakers ──
  const tb = s.tiebreakers.standings;
  const flip = tb.indexOf('coin-flip');
  if (flip !== -1 && flip !== tb.length - 1) err('tiebreakers.standings', 'Coin flip must be last');
  if (new Set(tb).size !== tb.length) err('tiebreakers.standings', 'A tiebreaker is listed twice');

  // ── Waivers ──
  if (s.waivers.system === 'faab' && !s.waivers.faab) err('waivers.faab', 'FAAB needs a budget');
  if (s.waivers.processing.schedule === 'weekly' && s.waivers.processing.weekday === null)
    err('waivers.processing.weekday', 'Weekly processing needs a weekday');
  // ON A DAY THE SPORT RUNS WAIVERS (B-509), in a league that has waivers. A
  // day exactly as it was in the version this replaces is not held to it
  // again, as a scoring rule is not, so a league that predates the rule can
  // still save anything else.
  const p = s.waivers.processing;
  const was = before?.waivers.processing;
  if (
    s.waivers.system !== 'none' &&
    p.schedule === 'weekly' &&
    p.weekday !== null &&
    !waiverWeekdaysOf(sport).includes(p.weekday) &&
    !(was?.schedule === 'weekly' && was.weekday === p.weekday)
  )
    err('waivers.processing.weekday', `Weekly claims process ${waiverDaysText(sport)}`);
  if (s.waivers.faab && s.waivers.faab.minBid > s.waivers.faab.budget)
    err('waivers.faab.minBid', 'Minimum bid is larger than the budget');
  for (const p of Object.keys(s.acquisitions.perPosition)) {
    if (!positions.has(p)) err('acquisitions.perPosition', `Unknown position ${p}`);
  }

  // ── Trades ──
  if (s.trades.deadlinePeriod !== null && s.trades.deadlinePeriod > rs.lastPeriod)
    err('trades.deadlinePeriod', 'Trade deadline is after the regular season');
  // The taxi squad closes in a week the league plays (§2.7 rule 4). One the
  // season does not have reads as its nearest (`taxiDeadlineWeek`), so a
  // warning: once the league drafts the roster is fixed, and a refusal here
  // would leave the schedule unsavable over a field nobody can change.
  const taxiCloses = s.roster.taxi?.deadlinePeriod ?? null;
  if (taxiCloses !== null && taxiCloses < rs.firstPeriod)
    warn(
      'roster.taxi.deadlinePeriod',
      `The regular season starts after Week ${taxiCloses}, so the taxi squad closes at its first kickoff`,
    );
  if (taxiCloses !== null && taxiCloses > rs.lastPeriod)
    warn(
      'roster.taxi.deadlinePeriod',
      `The regular season ends before Week ${taxiCloses}, so the taxi squad closes at Week ${rs.lastPeriod}’s first kickoff`,
    );
  if (s.trades.review === 'league-vote' && !s.trades.vetoThreshold)
    err('trades.vetoThreshold', 'League vote needs a veto threshold');
  if (s.trades.assets.faab && s.waivers.system !== 'faab')
    err('trades.assets.faab', 'FAAB can only be traded in a FAAB league');
  if (s.trades.assets.draftPicks && s.keepers.mode === 'redraft')
    warn('trades.assets.draftPicks', 'Pick trading in a redraft league only affects this draft');
  // PICKS AHEAD ARE PICKS (PLAN-2026-10-27-the-next-season §2.6 rule 2):
  // none is traded while draft picks are not.
  if (
    s.keepers.futurePickTrading > 0 &&
    s.keepers.mode !== 'redraft' &&
    !s.trades.assets.draftPicks
  )
    warn('keepers.futurePickTrading', 'Picks ahead are traded only when draft picks can be traded');

  // ── Draft ──
  const rosterSize = s.roster.slots.reduce((n, x) => n + x.count, 0);
  const carriedRosters = context.carried === true && s.keepers.mode === 'dynasty';
  if (s.draft.rounds < rosterSize && !carriedRosters)
    err('draft.rounds', `Fewer rounds (${s.draft.rounds}) than roster spots (${rosterSize})`);
  if (s.draft.rounds > rosterSize) warn('draft.rounds', 'More rounds than roster spots');
  // The format and the pick order cross here, not in the schema, which holds
  // shapes: an auction has no pick order and a pick draft needs one (the
  // 2026-09-19 decision).
  if (s.draft.format === 'auction' && !s.draft.auction)
    err('draft.auction', 'Auction draft needs auction settings');
  if (s.draft.format === 'auction' && s.draft.order !== null)
    err('draft.order', 'An auction has no pick order');
  if (s.draft.format === 'pick' && s.draft.order === null)
    err('draft.order', 'A pick draft needs a pick order');
  // EVERY ROSTER HAS TO FILL AT THE LOWEST BID, or the auction cannot end
  // (AUCTION.md §3.1).
  const auction = s.draft.format === 'auction' ? s.draft.auction : null;
  if (auction && auction.budget < s.draft.rounds * auction.minBid)
    err(
      'draft.auction.budget',
      `A $${String(auction.budget)} budget cannot fill ${String(s.draft.rounds)} spots at $${String(auction.minBid)} each`,
    );
  if (auction && auction.resetSeconds > auction.bidSeconds)
    err('draft.auction.resetSeconds', 'A late bid cannot put the clock back past where it starts');
  if (auction?.leftoverToWaivers && s.waivers.system !== 'faab')
    err(
      'draft.auction.leftoverToWaivers',
      'Money left over joins a waiver budget only when waivers are bid on',
    );
  if (s.draft.mode === 'live' && s.draft.pickSeconds < 30 && teams > 12)
    warn('draft.pickSeconds', 'Under 30 seconds with a big league is hectic');
  // A STOP THAT STARTS AND ENDS AT ONCE stops nothing, and the rules page
  // would say it stops (the review of B-341).
  if (s.draft.overnightPause && s.draft.overnightPause.from === s.draft.overnightPause.to)
    err('draft.overnightPause', 'The clock cannot stop and start again at the same time.');
  if (s.draft.seatOrder === 'manual' && s.draft.manualOrder.length !== teams)
    err('draft.manualOrder', `Manual order must list all ${teams} teams`);

  // ── Keepers ──
  if (s.keepers.mode === 'redraft' && s.keepers.keeperCount > 0)
    err('keepers.keeperCount', 'Redraft leagues have no keepers');
  if (s.keepers.mode !== 'redraft' && s.keepers.keeperCount === 0 && s.keepers.mode === 'keeper')
    warn('keepers.keeperCount', 'Keeper league with zero keepers');
  // A KEEPER'S COST IN THE DRAFT'S OWN TERMS (PLAN-2026-10-27-the-next-season
  // §2.3): a round in a draft by picks, a price in an auction.
  if (s.keepers.mode === 'keeper') {
    if (s.keepers.cost === 'auction-value' && s.draft.format !== 'auction')
      err('keepers.cost', 'A keeper’s auction price needs an auction draft');
    if (
      (s.keepers.cost === 'draft-round' || s.keepers.cost === 'draft-round-escalating') &&
      s.draft.format === 'auction'
    )
      err('keepers.cost', 'A keeper’s round needs a draft by picks');
    if (s.keepers.keeperCount > s.draft.rounds)
      err('keepers.keeperCount', 'A team cannot keep more players than the draft has rounds');
  }
  if (s.roster.taxi && s.keepers.mode !== 'dynasty')
    warn('roster.taxi', 'Taxi squads are usually a dynasty feature');

  return issues;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The days a weekly waiver run may fall on, 0 for Sunday: the sport's, or any day (B-509). */
export function waiverWeekdaysOf(sport: SportDefinition): readonly number[] {
  return sport.calendar.waiverWeekdays ?? [0, 1, 2, 3, 4, 5, 6];
}

/** Those days in words: "Wednesday through Saturday", "on Tuesday or Friday", "any day". */
export function waiverDaysText(sport: SportDefinition): string {
  const days = [...waiverWeekdaysOf(sport)].sort((a, b) => a - b);
  if (days.length === 7) return 'any day';
  const run = days.every((d, i) => i === 0 || d === days[i - 1]! + 1);
  if (run && days.length > 2)
    return `${DAY_NAMES[days[0]!]} through ${DAY_NAMES[days[days.length - 1]!]}`;
  const names = days.map((d) => DAY_NAMES[d]!);
  return `on ${names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}` : names[0]}`;
}
